/*
    Copyright © 2025 TRIPLES INNOVATION. All rights reserved.
    Proprietary and confidential. This source code is the exclusive property of
    TRIPLES INNOVATION and protected by copyright and trade secret law.
    Any unauthorized use, copying, modification, distribution, or disclosure
    is strictly prohibited without prior written permission from TRIPLES INNOVATION.
    Use is subject to a written license agreement with TRIPLES INNOVATION.
*/

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Typography,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import BarChartIcon from "@mui/icons-material/BarChart";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { styled, useTheme } from '@mui/material/styles';
import SurfaceSection from "../Common/SurfaceSection";
import CircularProgress from '@mui/material/CircularProgress';
import { CarsPerStationServerResponse } from "../../types/Reports";
import useReportsService from "../../services/useReportsService";
import LineChart from "../Common/Charts/LineChart";
import { getMonthsShort } from "../../utils/time";
import useToast, { ToastPosition, ToastType } from "../../hooks/useToast";
import EmptyState from "../EmptyState/EmptyState";
import CenteredBox from "../Common/CenteredBox";

// TODO this is not truly needed, we use it more for the ref. Let's delete it
const ChartSurface = styled(SurfaceSection)`
    align-items: center;
    display: flex;
    justify-content: center;
`;

// TODO make it re-usable along other staff such as loading state, empty state, subtitle etc
const CloseButton = styled(IconButton)(({ theme }) => `
    color: ${theme.palette.primary[100]};
    position: absolute;
    right: ${theme.spacing(2)};
    top: ${theme.spacing(2)};
`);

// Same here, let's make it re-usable. Maybe make 2-3 report styles and use them
const StyledDialogContent = styled(DialogContent)`
    display: flex;
    flex-direction: column;
    height: 100%;
`;

// Same here, re-usable
const StyledTable = styled(Table)(({ theme }) => `
    border-bottom: 1px solid ${theme.palette.divider};
`);

// Same here, re-usable
const TableSection = styled(Box)`
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: flex-end;
`;

function CarsPerStationComparisonReportDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
    const [reportData, setReportData] = useState<CarsPerStationServerResponse | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const lineChartParentRef = useRef<HTMLDivElement>(null);

    const theme = useTheme();
    const { t } = useTranslation("common");
    const { fetchCarsPerStationComparisonReportData } = useReportsService();
    const { showToast } = useToast();

    useEffect(() => {
        if (!open) return;
        setIsLoading(true);
        fetchCarsPerStationComparisonReportData()
            .then((data) => {
                setReportData(data);
            })
            .catch(() => {
                showToast({
                    message: t("common.unknownError"),
                    type: ToastType.ERROR,
                    position: ToastPosition.TOP_CENTER,
                });
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [open, fetchCarsPerStationComparisonReportData, showToast, t]);

    const entries = useMemo(() => {
        if (!reportData?.carsPerStation) return [];
        const monthsShort = getMonthsShort(t);
        return Object.keys(reportData.carsPerStation).map(Number).flatMap((year) =>
            Object.keys(reportData.carsPerStation[year]).map(Number).map((month) => ({
                monthName: `${monthsShort[month - 1]} ${year}`,
                ...reportData.carsPerStation[year][month],
            }))
        );
    }, [reportData]);

    const noData = entries.length === 0;

    const table = (
        <StyledTable size="small">
            <TableHead>
                <TableRow>
                    <TableCell align="center">
                        <Typography variant="body2" fontWeight={800}>
                            {t("reports.carsPerStation.month")}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography variant="body2" fontWeight={800}>
                            {t("reports.carsPerStation.myLocationAvg")}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography variant="body2" fontWeight={800}>
                            {t("reports.carsPerStation.marketAvg")}
                        </Typography>
                    </TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {entries.map((row) => (
                    <TableRow key={row.monthName}>
                        <TableCell align="center">
                            <span>{row.monthName}</span>
                        </TableCell>
                        <TableCell align="center">{row.own}</TableCell>
                        <TableCell align="center">{row.othersAverage}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </StyledTable>
    );

    const renderContent = () => {
        if (isLoading) {
            return (
                <CenteredBox>
                    <CircularProgress />
                </CenteredBox>
            );
        }

        if (noData) {
            return (
                <EmptyState title={t("reports.carsPerStation.no-data")} icon={<BarChartIcon sx={{ fontSize: 48 }} />} />
            );
        }

        return (
            <Grid container spacing={2} flex={1}>
                <Grid size={{ xs: 12, md: 3 }} display={{ xs: 'none', md: 'block' }}>
                    <TableSection>
                        {table}
                    </TableSection>
                </Grid>
                <Grid size={{ xs: 12, md: 9 }}>
                    <ChartSurface ref={lineChartParentRef}>
                        <LineChart
                            parentRef={lineChartParentRef}
                            colorPalette={[
                                theme.palette.primary.light,
                                theme.palette.primary.dark,
                            ]}
                            topBottomPadding={16}
                            leftRightPadding={16}
                            data={{
                                xAxis: {
                                    data: entries.map((item) => item.monthName),
                                },
                                series: [
                                    {
                                        id: 'own',
                                        label: t("reports.carsPerStation.myLocationAvg"),
                                        data: entries.map((item) => item.own),
                                        showMark: true,
                                        area: true,
                                    },
                                    {
                                        id: 'othersAverage',
                                        label: t("reports.carsPerStation.marketAvg"),
                                        data: entries.map((item) => item.othersAverage),
                                        showMark: true,
                                    },
                                ],
                                legendItems: [
                                    {
                                        color: theme.palette.primary.light,
                                        label: t("reports.carsPerStation.myLocationAvg"),
                                    },
                                    {
                                        color: theme.palette.primary.dark,
                                        label: t("reports.carsPerStation.marketAvg"),
                                    },
                                ],
                            }}
                            chartInitialSize={{ width: 800, height: 400 }}
                        />
                    </ChartSurface>
                </Grid>
                <Grid size={12} display={{ xs: 'block', md: 'none' }}>
                    {table}
                </Grid>
            </Grid>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} fullScreen>
            <DialogTitle>
                {t("reports.carsPerStation.title")}
                <CloseButton aria-label="close" onClick={onClose}>
                    <CloseIcon />
                </CloseButton>
            </DialogTitle>
            <StyledDialogContent>
                <Typography mb={2} variant="subtitle2">
                    {t("reports.carsPerStation.subtitle")}
                </Typography>
                {renderContent()}
            </StyledDialogContent>
        </Dialog>
    );
}

export default CarsPerStationComparisonReportDialog;
